import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Ruler as RulerIcon,
  Ruler,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Loader2,
  History,
  Upload,
  Save,
} from 'lucide-react';
import { StatsCard, TamanhoCard, TAMANHO_COLORS } from './StatsCard';
import { SearchInput } from './SearchInput';
import { LojistasTable } from './LojistasTable';
import { TamanhoPagination } from './TamanhoPagination';
import { HistoryModal } from './HistoryModal';
import { ImportModal } from './ImportModal';
import { fetchTamanhos, batchUpdateTamanhos, fetchTamanhoHistory, fetchLojistas } from './api';
import { TamanhoSummary, HistoryEntry, Lojista, Pagination, TAMANHO_UL_DROPDOWN_OPTIONS } from './types';
import { PageTitle } from './../../../../_metronic/layout/core';

const TAMANHO_DISPLAY_OPTIONS = Array.from(TAMANHO_UL_DROPDOWN_OPTIONS);
const SEARCH_DEBOUNCE_MS = 800;

const TamanhoScreen: React.FC = () => {
  const [tamanhos, setTamanhos] = useState<TamanhoSummary[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<HistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedTamanho, setSelectedTamanho] = useState<string | null>(null);

  const [lojistas, setLojistas] = useState<Lojista[]>([]);
  const [lojistasLoading, setLojistasLoading] = useState(true);
  const [lojistasError, setLojistasError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0,
  });

  const [editedTamanhos, setEditedTamanhos] = useState<Map<string, string>>(new Map());
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const currentPageRef = useRef(1);
  const currentSearchRef = useRef('');
  const currentTamanhoRef = useRef<string | null>(null);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      currentPageRef.current = 1;
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const loadSummary = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await fetchTamanhos();
      setTamanhos(data);
    } catch {
      setLoadError('Erro ao carregar os tamanhos. Verifique a conexão e tente novamente.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadLojistas = useCallback(async (page: number, search: string, tamanho?: string | null) => {
    setLojistasLoading(true);
    setLojistasError(null);
    try {
      const res = await fetchLojistas(page, search, tamanho || undefined);
      setLojistas(res.data ?? []);
      setPagination(res.pagination);
    } catch {
      setLojistasError('Erro ao carregar lojistas.');
      setLojistas([]);
    } finally {
      setLojistasLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    currentPageRef.current = pagination.page;
    currentSearchRef.current = debouncedSearch;
    currentTamanhoRef.current = selectedTamanho;
    loadLojistas(pagination.page, debouncedSearch, selectedTamanho);
  }, [pagination.page, debouncedSearch, selectedTamanho, loadLojistas]);

  const handlePageChange = useCallback((page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  }, []);

  const handleTamanhoCardClick = useCallback((tamanho: string) => {
    setSelectedTamanho((prev) => {
      const next = prev === tamanho ? null : tamanho;
      setPagination((p) => ({ ...p, page: 1 }));
      return next;
    });
  }, []);

  const handleRefresh = useCallback(() => {
    loadSummary();
    loadLojistas(currentPageRef.current, currentSearchRef.current, currentTamanhoRef.current);
  }, [loadSummary, loadLojistas]);

  const totalCnpjs = useMemo(
    () => tamanhos.reduce((sum, t) => sum + t.totalCnpjs, 0),
    [tamanhos]
  );

  const tamanhoDistribution = useMemo(() => {
    const dist: Record<string, number> = {};
    TAMANHO_DISPLAY_OPTIONS.forEach((o) => { dist[o] = 0; });
    tamanhos.forEach((t) => {
      if (dist[t.tamanhoUL] !== undefined) {
        dist[t.tamanhoUL] = t.totalCnpjs;
      }
    });
    return dist;
  }, [tamanhos]);

  const defaultTamanhoColor = TAMANHO_COLORS['Não Informado'];

  const handleTamanhoChange = useCallback(
    (cnpj: string, newTamanho: string) => {
      const original = lojistas.find((l) => l.CNPJLojista === cnpj);
      if (!original) return;

      const next = new Map(editedTamanhos);
      if (newTamanho === original.tamanho_ul) {
        next.delete(cnpj);
      } else {
        next.set(cnpj, newTamanho);
      }
      setEditedTamanhos(next);
      setSaveSuccess(false);
      setSaveError(null);
    },
    [lojistas, editedTamanhos]
  );

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    const items = Array.from(editedTamanhos.entries()).map(([cnpjlojista, tamanho_ul]) => ({
      cnpjlojista,
      tamanho_ul,
    }));

    try {
      const res = await batchUpdateTamanhos(items);
      if (!res?.success) {
        setSaveError(res?.error || 'Erro ao salvar as alterações');
      } else {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch {
      setSaveError('Falha na conexão com o servidor');
    }

    setEditedTamanhos(new Map());
    setIsSaving(false);
    await Promise.all([
      loadSummary(),
      loadLojistas(currentPageRef.current, currentSearchRef.current, currentTamanhoRef.current),
    ]);
  };

  const changesCount = editedTamanhos.size;

  const handleOpenHistory = useCallback(async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryData([]);
    try {
      const entries = await fetchTamanhoHistory();
      setHistoryData(entries);
    } catch {
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleImportBatch = async (items: { cnpjlojista: string; tamanho_ul: string }[]) => {
    return await batchUpdateTamanhos(items);
  };

  const handleImportClose = () => {
    setImportOpen(false);
    loadSummary();
    loadLojistas(1, debouncedSearch, selectedTamanho);
  };

  return (
    <>
      <PageTitle>Tamanhos de Loja</PageTitle>
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <header className="mb-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-xl font-bold text-gray-900 tracking-tight">
                  Gerencie os Tamanhos UL dos Lojistas
                </h1>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={handleRefresh}
                  disabled={isLoading || lojistasLoading}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-50"
                  aria-label="Recarregar dados"
                >
                  <RefreshCw className={`w-4 h-4 ${(isLoading || lojistasLoading) ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => setImportOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all"
                >
                  <Upload className="w-4 h-4" />
                  <span className="hidden sm:inline">Importar</span>
                </button>
                <button
                  onClick={handleOpenHistory}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all"
                >
                  <History className="w-4 h-4" />
                  <span className="hidden sm:inline">Histórico</span>
                </button>
                <button
                  onClick={handleSave}
                  disabled={changesCount === 0 || isSaving}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-blue-600/20 disabled:shadow-none"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Salvar
                    </>
                  )}
                </button>
              </div>
            </div>

            {changesCount > 0 && (
              <div className="mt-5 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
                <div className="bg-amber-100 p-1.5 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                </div>
                <p className="text-sm font-medium text-amber-800">
                  {changesCount} {changesCount === 1 ? 'lojista com alteração pendente' : 'lojistas com alterações pendentes'}
                </p>
              </div>
            )}

            {saveSuccess && (
              <div className="mt-5 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3">
                <div className="bg-emerald-100 p-1.5 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                </div>
                <p className="text-sm font-medium text-emerald-800">
                  Alterações salvas com sucesso
                </p>
              </div>
            )}

            {saveError && (
              <div className="mt-5 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                <div className="bg-red-100 p-1.5 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                </div>
                <p className="text-sm font-medium text-red-800">{saveError}</p>
              </div>
            )}
          </header>

          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-4">
            <StatsCard
              title="Total CNPJs"
              value={totalCnpjs}
              description="Lojistas cadastrados"
              icon={<RulerIcon className="w-5 h-5" />}
            />
            <StatsCard
              title="Categorias"
              value={tamanhos.length}
              description="Tamanhos distintos"
              icon={<Ruler className="w-5 h-5" />}
            />
            <StatsCard
              title="Alterações Pendentes"
              value={changesCount}
              description={changesCount === 0 ? 'Nenhuma alteração' : 'Aguardando salvar'}
              icon={<AlertTriangle className="w-5 h-5" />}
              variant={changesCount > 0 ? 'warning' : 'default'}
            />
          </div>

          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-8 mb-8">
            {TAMANHO_DISPLAY_OPTIONS.map((tamanho) => (
              <TamanhoCard
                key={tamanho}
                name={tamanho}
                count={tamanhoDistribution[tamanho] ?? 0}
                total={totalCnpjs}
                color={TAMANHO_COLORS[tamanho] ?? defaultTamanhoColor}
                icon={<Ruler className="w-4 h-4" />}
                active={selectedTamanho === tamanho}
                onClick={() => handleTamanhoCardClick(tamanho)}
              />
            ))}
          </div>

          <div className="mb-5">
            <SearchInput
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Buscar por CNPJ, razão social ou nome fantasia..."
            />
          </div>

          {loadError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex flex-col items-center gap-3 mb-5">
              <AlertCircle className="w-8 h-8 text-red-400" />
              <p className="text-sm text-red-700 text-center">{loadError}</p>
              <button
                onClick={handleRefresh}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Tentar novamente
              </button>
            </div>
          )}

          {lojistasError && !loadError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex flex-col items-center gap-3 mb-5">
              <AlertCircle className="w-8 h-8 text-red-400" />
              <p className="text-sm text-red-700 text-center">{lojistasError}</p>
              <button
                onClick={() => loadLojistas(pagination.page, debouncedSearch, selectedTamanho)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Tentar novamente
              </button>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <LojistasTable
              data={lojistas}
              loading={lojistasLoading}
              editedTamanhos={editedTamanhos}
              onTamanhoChange={handleTamanhoChange}
            />
            {!lojistasLoading && !lojistasError && (
              <TamanhoPagination
                pagination={pagination}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        </div>
      </div>

      <HistoryModal
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        history={historyData}
        loading={historyLoading}
      />

      <ImportModal
        isOpen={importOpen}
        onClose={handleImportClose}
        onImportComplete={handleImportBatch}
      />
    </>
  );
};

export default TamanhoScreen;